import { useEffect, useRef, useState } from 'react'
import { Gift } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import type { StampMyDraw } from '../services/api'
import LotteryResultCard from './LotteryResultCard'

/** くじを回している時間。**短くする** — 会場では列ができるので、
 *  200 人 × 5 秒 は運営が困る。1 秒強で「引いた感」は十分出る。 */
const SPIN_MS = 1300

/**
 * #1871 抽選の演出。**結果はサーバが決めたものをそのまま出すだけ。**
 *
 * ここで乱数は回さない (回すと画面と記録がズレる)。演出はあくまで見せ方で、
 * 途中で閉じても結果はサーバに残っており、ラリー画面から何度でも確認できる。
 *
 * **動画は使わない。** 会場は電波が悪く、PWA の precache 予算も食う
 * (#1391 で 2 MiB に戻した経緯)。CSS なら数百バイトで、オフラインでも確実に動く。
 *
 * タップで飛ばせる。`prefers-reduced-motion` の端末では演出せず即結果を出す。
 */
export default function LotteryDrawReveal({ draw }: { draw: StampMyDraw }) {
  const { t } = useI18n()
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  // justDrawn でない = 前のスキャンで引いた結果。**回し直さない**
  const [spinning, setSpinning] = useState(() => !!draw.justDrawn && !reduced)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!spinning) return
    timerRef.current = window.setTimeout(() => setSpinning(false), SPIN_MS)
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    }
  }, [spinning])

  if (!spinning) {
    return (
      <div className={draw.justDrawn && !reduced ? 'animate-lottery-burst' : ''}>
        <LotteryResultCard draw={draw} />
        {draw.result === 'win' && !reduced && <WinConfetti />}
      </div>
    )
  }

  return (
    <button
      onClick={() => setSpinning(false)}
      className="w-full rounded-2xl bg-navy/5 px-4 py-6 text-center"
      aria-live="polite"
      aria-label={t('stampRally.lotteryDrawing')}
    >
      <div className="flex justify-center">
        <div className="animate-lottery-shake flex h-16 w-16 items-center justify-center rounded-2xl bg-gold/20">
          <Gift size={30} className="text-gold" />
        </div>
      </div>
      <p className="mt-3 font-display text-sm text-navy">{t('stampRally.lotteryDrawing')}</p>
      <p className="mt-0.5 font-body text-[11px] text-text-tertiary">
        {t('stampRally.lotterySkip')}
      </p>
    </button>
  )
}

/** 当たりの紙吹雪。ProfileSetup と同じ wallet-confetti を使う (追加 CSS 無し)。 */
function WinConfetti() {
  const [particles] = useState(() =>
    Array.from({ length: 24 }, () => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      color: ['#D4AF37', '#E60033', '#1B2A4A', '#7FB069'][Math.floor(Math.random() * 4)],
    })),
  )
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden>
      {particles.map((p, i) => (
        <span
          key={i}
          className="wallet-confetti absolute h-2 w-2 rounded-full"
          style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, backgroundColor: p.color }}
        />
      ))}
    </div>
  )
}
